/**
 * use-share-realtime — subscribe to the per-user shares channel.
 *
 * `external-sharing` Phase 3 Session 3.1 (2026-05-09).
 *
 * Subscribes to `user:<userId>:shares` (Phase 1 RLS:
 * `realtime_user_shares_subscribe_self` policy gates on auth.uid() = user_id;
 * only the channel owner can subscribe). Phase 2's `SharePublisher` emits 3
 * events:
 *   - `share_link_received` — fires on `createShare` when recipient resolved
 *     internally; also re-fires on `linkSharesForUser` when an external
 *     recipient signs up and the dual-write resolves the row.
 *   - `share_link_forked_back` — fires on `shareForkBack` to the original
 *     owner.
 *   - `share_link_revoked` — fires on `revokeShare` to the recipient (owner
 *     drives the optimistic update via `useRevokeShare` directly).
 *
 * Every inbound event invalidates the relevant TanStack caches so the badge
 * + lists re-sync. Polling fallback fires every 5min (NOT 60s — meaningfully
 * cheaper than spec's original 60s poll per /spec-review MINOR #18) when
 * Realtime drops or `VITE_REALTIME_ENABLED=false`. Only invalidates the
 * unread-count query in the polling path (cheap single endpoint); fan-out
 * to `received()` only if the count number changed (server is already the
 * source of truth, so a change indicates a missed echo worth full re-sync).
 *
 * Mounted ONCE at `<RootLayout>` for the side-effect subscription. Returns
 * `{status: RealtimeStatus}` for diagnostics; consumers don't typically read
 * it (badge consumes `useUnreadShareCount` directly).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import { getUnreadCount } from '@/lib/shares-api';

const REALTIME_ENABLED = import.meta.env.VITE_REALTIME_ENABLED === 'true';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5min — explicit /spec-review MINOR #18 reduction.
const RECONNECT_BACKOFF_MS = [1000, 2000, 5000, 10_000] as const;
const RECONNECT_MAX_ATTEMPTS = 5;

export type ShareRealtimeStatus =
  | 'connected'
  | 'reconnecting'
  | 'polling'
  | 'offline'
  | 'idle';

const SHARE_EVENTS = [
  'share_link_received',
  'share_link_forked_back',
  'share_link_revoked',
] as const;

type ShareEvent = (typeof SHARE_EVENTS)[number];

export interface UseShareRealtimeReturn {
  status: ShareRealtimeStatus;
}

export function useShareRealtime(): UseShareRealtimeReturn {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const [status, setStatus] = useState<ShareRealtimeStatus>(() => {
    if (!REALTIME_ENABLED) return 'polling';
    return 'idle';
  });

  // Last-seen unread count drives the polling-path "did the count change?"
  // gate that decides whether to additionally invalidate the lists.
  const lastUnreadRef = useRef<number | null>(null);

  const invalidateOnEvent = useCallback(
    (event: ShareEvent) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.shares.unreadCount() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.shares.received() });
      if (event === 'share_link_revoked') {
        // Owner-side cache also gets a free rebuild when the server confirms
        // (covers the cross-tab + multi-device case where the owner revoked
        // on another surface).
        void queryClient.invalidateQueries({ queryKey: queryKeys.shares.sent() });
      }
    },
    [queryClient],
  );

  // ---------------------------------------------------------------------------
  // Realtime subscription with capped exponential backoff.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!userId || !REALTIME_ENABLED) return;

    let disposed = false;
    let pendingTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempt = 0;
    type Channel = ReturnType<typeof supabase.channel>;
    let currentChannel: Channel | null = null;

    setStatus('reconnecting');

    const subscribe = (): void => {
      if (disposed) return;
      const topic = `user:${userId}:shares`;
      // `private: true` is REQUIRED for cross-cluster broadcast delivery —
      // matches the realtime-collab-foundation Phase 4 lesson: server-side
      // service-role publishes only reach user-JWT subscribers on other
      // cluster nodes when the channel is configured private.
      const channel = supabase.channel(topic, { config: { private: true } });
      currentChannel = channel;

      for (const event of SHARE_EVENTS) {
        channel.on('broadcast', { event }, () => {
          if (disposed) return;
          invalidateOnEvent(event);
        });
      }

      channel.subscribe((subStatus) => {
        if (disposed) return;
        if (subStatus === 'SUBSCRIBED') {
          setStatus('connected');
          reconnectAttempt = 0;
        } else if (
          subStatus === 'CHANNEL_ERROR' ||
          subStatus === 'CLOSED' ||
          subStatus === 'TIMED_OUT'
        ) {
          if (reconnectAttempt >= RECONNECT_MAX_ATTEMPTS) {
            setStatus('offline');
            return;
          }
          setStatus('reconnecting');
          const delay =
            RECONNECT_BACKOFF_MS[
              Math.min(reconnectAttempt, RECONNECT_BACKOFF_MS.length - 1)
            ];
          reconnectAttempt++;
          pendingTimer = setTimeout(() => {
            if (disposed) return;
            void Promise.resolve(channel.unsubscribe()).catch(() => {});
            subscribe();
          }, delay);
        }
      });
    };

    subscribe();

    return () => {
      disposed = true;
      if (pendingTimer) clearTimeout(pendingTimer);
      if (currentChannel) {
        // Wrap in Promise.resolve so a sync-throw OR non-promise return from
        // a stubbed/older Realtime client doesn't crash the cleanup. We don't
        // care about the unsubscribe outcome — we're tearing down.
        void Promise.resolve(currentChannel.unsubscribe()).catch(() => {});
      }
    };
  }, [userId, invalidateOnEvent]);

  // ---------------------------------------------------------------------------
  // Polling fallback — runs when the build-time flag is OFF, OR after the
  // 5th reconnect failure. 5min interval; cheap single endpoint
  // (`getUnreadCount`); only fans out to `received()` invalidation if the
  // count number actually changed (server-authoritative comparison —
  // any change means we missed an echo).
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!userId) return;
    const shouldPoll = !REALTIME_ENABLED || status === 'offline';
    if (!shouldPoll) return;

    if (!REALTIME_ENABLED) setStatus('polling');
    let disposed = false;

    const tick = async (): Promise<void> => {
      if (disposed) return;
      try {
        const res = await getUnreadCount();
        const changed =
          lastUnreadRef.current !== null &&
          lastUnreadRef.current !== res.unreadCount;
        lastUnreadRef.current = res.unreadCount;
        // Always update the unread-count cache from the polled value so the
        // badge re-renders without an extra fetch.
        queryClient.setQueryData(queryKeys.shares.unreadCount(), res);
        if (changed) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.shares.received() });
        }
      } catch (e) {
        console.warn('[share-realtime] polling getUnreadCount failed', e);
      }
    };

    void tick();
    const interval = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      disposed = true;
      clearInterval(interval);
    };
  }, [userId, status, queryClient]);

  return { status };
}
